import { useContext } from "react"
import { ConfigStateContext } from "shared-components/Contexts/Configurations/context"
import { useSEO } from "shared-components/Components/SEO/hook"
import Head from "next/head"
import { sinVacios, sitioWebJson } from "shared-components/Components/SEO/entidadSitio"

// La UI muestra «Consultar» bajo este umbral (PriceTag/web.tsx) — el markup replica ese gate.
const PRECIO_MINIMO_VISIBLE = 5

const numeroONada = v => {
    if (v === null || v === undefined || v === "") return undefined
    const n = Number(v)
    return Number.isFinite(n) && n >= 0 ? n : undefined
}

// property_type.id → subtipo de Accommodation (schema.org); el resto queda en el genérico.
const tiposSchema = {
    "1": "House", "15": "House", "16": "House", "17": "House",
    "2": "Apartment", "14": "Apartment",
    "18": "Room",
}

const currenciesCodes = {
    "1": "USD", "2": "UYU", "3": "PYG",
    "4": "COP", "5": "BOB", "6": "PEN",
    "7": "UYR", "8": "UYI", "9": "BRL",
    "10": "ARS", "11": "EUR",
}

// Un aviso del listado → el mini-markup del patrón de la ficha (INF-364): doble tipo
// RealEstateListing+Product con el inmueble físico anidado en mainEntity.
const avisoJson = (e, posicion, main_domain, country_code) => {
    const price = e?.price?.amount
    const priceCurrency = currenciesCodes[e?.price?.currency?.id]
    const activo = e?.active !== false && e?.active !== 0

    const offers =
        activo && priceCurrency && !e?.price?.hidePrice && Number(price) >= PRECIO_MINIMO_VISIBLE
            ? {
                  "@type": "Offer",
                  "price": price,
                  "priceCurrency": priceCurrency,
                  "availability": "https://schema.org/InStock",
              }
            : undefined

    const address = sinVacios({
        "@type": "PostalAddress",
        "streetAddress": e?.showAddress ? e?.address : undefined,
        "addressLocality":
            e?.locations?.city?.[0]?.name ||
            e?.locations?.locality?.[0]?.name ||
            e?.locations?.neighbourhood?.[0]?.name,
        "addressRegion": e?.locations?.state?.[0]?.name,
        "addressCountry": /^[A-Z]{2}$/.test(country_code || "") ? country_code : undefined,
    })
    // Un PostalAddress solo con el país de la marca no aporta: se exige algún dato del aviso.
    const addressConDato = Object.keys(address).length > (address.addressCountry ? 2 : 1)

    const lat = parseFloat(e?.latitude)
    const lng = parseFloat(e?.longitude)
    // Con pointType el mapa muestra zona aproximada: no se publica la coordenada exacta.
    const geo =
        !e?.pointType && Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0)
            ? {
                  "@type": "GeoCoordinates",
                  "latitude": lat,
                  "longitude": lng,
              }
            : undefined

    const m2 = numeroONada(e?.m2)
    const nombre = e?.title?.replace(/\s*\[No disponible\]\s*$/, "")
    // Mismo link que la tarjeta (ListingCard): el de la unidad de un proyecto redirige al proyecto.
    const link = e?.project?.id ? e.project.link : e?.link
    const url =
        typeof link === "string" && link.length > 0
            ? "https://" + main_domain + (link.startsWith("/") ? "" : "/") + link
            : undefined

    const inmueble = sinVacios({
        "@type": tiposSchema[e?.property_type?.id] || "Accommodation",
        "name": nombre,
        "numberOfBedrooms": numeroONada(e?.bedrooms),
        "numberOfBathroomsTotal": numeroONada(e?.bathrooms),
        "floorSize":
            m2 > 0
                ? { "@type": "QuantitativeValue", "value": m2, "unitCode": "MTK" }
                : undefined,
        "address": addressConDato ? address : undefined,
        "geo": geo,
    })
    // Sin ningún dato físico (solo @type y name) el sub-objeto no aporta: se omite.
    const mainEntity = Object.keys(inmueble).length > (inmueble.name ? 2 : 1) ? inmueble : undefined

    return sinVacios({
        // El doble tipo solo con oferta: un Product sin offers es inválido para Google
        // (Search Console lo marcaría en cada SERP con un aviso «Consultar»).
        "@type": offers ? ["RealEstateListing", "Product"] : "RealEstateListing",
        "position": posicion,
        "name": nombre,
        "url": url,
        "image": e?.img,
        "offers": offers,
        "mainEntity": mainEntity,
    })
}

export const ListingStructDataJson = ({ listings }) => {
    const config = useContext(ConfigStateContext)
    const { main_domain, country_code } = config
    const { seoMetaTags } = useSEO()

    // Blindaje: algunos montajes históricos pasan un objeto en vez del array de avisos.
    const avisos = Array.isArray(listings) ? listings.filter(i => !i?.isEspecial) : []
    // Sin avisos no se emite nada (la versión anterior imprimía "false" en el script).
    if (avisos.length === 0) return null

    const jsonData = sinVacios({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": seoMetaTags?.h1,
        "description": seoMetaTags?.description,
        "url": seoMetaTags?.canonical,
        "mainEntity": {
            "@type": "ItemList",
            "name": seoMetaTags?.h1,
            "numberOfItems": avisos.length,
            "itemListOrder": "https://schema.org/ItemListUnordered",
            "itemListElement": avisos.map((e, i) => avisoJson(e, i + 1, main_domain, country_code)),
        },
        "isPartOf": sitioWebJson(config),
    })

    return <>
        <Head>
             <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    // El escape de < evita que un </script> inyectado en la data corte el bloque (XSS).
                    __html: JSON.stringify(jsonData).replace(/</g, "\\u003c"),
                }}></script>
        </Head>
    </>
}
